import { useState, type ComponentType, type ReactNode } from 'react'
import {
  LogOut,
  LifeBuoy,
  Sparkles,
  KeyRound,
  Fingerprint,
} from 'lucide-react'
import { SupportVerificationCodeDialog } from '@/common/support-verification-code'
import { useFeatureFlags, setNewUiEnabled } from '@/hooks/use-feature-flags'
import { useAccess } from '@/access'
import PinAuth from '@/pages/[org_id]/account/organisation/pin-auth'
import {
  orgNavigationItems,
  profileOrgNavigationItems,
} from '@/components/SideNav/menu-config'

type AccountIcon = ComponentType<{ className?: string }>

interface AccountMenuItemBase {
  id: string
  label: string
  icon: AccountIcon
  tone?: 'destructive'
}

export type AccountMenuItem =
  | (AccountMenuItemBase & { kind: 'link'; to: string })
  | (AccountMenuItemBase & { kind: 'external'; href: string })
  | (AccountMenuItemBase & { kind: 'action'; onSelect: () => void })

export interface AccountMenuSection {
  id: string
  items: AccountMenuItem[]
}

/**
 * THE account menu — one source of truth for every surface that shows it
 * (the desktop sidebar footer dropdown and the mobile nav panel's account
 * view), so the two can't drift.
 *
 * `dialogs` is the markup the actions need (PIN auth, support verification);
 * the hook owns their open state, so a consumer just renders `{dialogs}`.
 */
export function useAccountMenu(orgId: string): {
  sections: AccountMenuSection[]
  dialogs: ReactNode
} {
  const { newUi } = useFeatureFlags()
  const { can } = useAccess()
  const [pinAuthOpen, setPinAuthOpen] = useState(false)
  const [supportCodeOpen, setSupportCodeOpen] = useState(false)

  // The account options — same source as the old profile/account menu so they
  // stay in sync. Keys are relative to the org account context: `organisation/*`
  // pages live under `account/organisation/*`; `account/*` and `marketplace/*`
  // sit at the org root; `/` is switch-organisation.
  //
  // `profileOrgNavigationItems` (Profile, Notifications) are the user's OWN
  // settings and stay visible to every role. The org administration items are
  // admin-only, matching the `org.account` guard on the route — a link that
  // bounces you is worse than no link.
  const canManageOrg = can('org.account')
  const hrefFor = (key: string) => {
    if (key === '/') return '/'
    if (key.startsWith('organisation/')) return `/${orgId}/account/${key}`
    return `/${orgId}/${key}`
  }
  const toLinkItem = (
    item: (typeof orgNavigationItems)[number]
  ): AccountMenuItem => ({
    id: item.key,
    label: item.title,
    icon: item.icon,
    kind: 'link',
    to: hrefFor(item.key),
  })

  const accountItems: AccountMenuItem[] = [
    ...profileOrgNavigationItems.map(toLinkItem),
    {
      id: 'verify-identity',
      label: 'Verify my identity',
      icon: Fingerprint,
      kind: 'action',
      onSelect: () => {
        setSupportCodeOpen(true)
      },
    },
    ...(canManageOrg ? orgNavigationItems.map(toLinkItem) : []),
    {
      id: 'pin-auth',
      label: 'Authenticate with PIN',
      icon: KeyRound,
      kind: 'action',
      onSelect: () => {
        setPinAuthOpen(true)
      },
    },
  ]

  const systemItems: AccountMenuItem[] = []
  if (newUi.access === 'stampede-only') {
    systemItems.push({
      id: 'ui-toggle',
      label: newUi.enabled ? 'Back to old UI' : 'Try new UI',
      icon: Sparkles,
      kind: 'action',
      onSelect: () => {
        setNewUiEnabled(!newUi.enabled)
      },
    })
  }
  systemItems.push({
    id: 'support',
    label: 'Help',
    icon: LifeBuoy,
    kind: 'external',
    href: 'https://stampede.ai/knowledge-base',
  })

  const sections: AccountMenuSection[] = [
    { id: 'account', items: accountItems },
    { id: 'system', items: systemItems },
    {
      id: 'session',
      items: [
        {
          id: 'logout',
          label: 'Log out',
          icon: LogOut,
          kind: 'link',
          to: '/oauth/logout',
          tone: 'destructive',
        },
      ],
    },
  ].filter((section) => section.items.length > 0)

  const dialogs = (
    <>
      <PinAuth
        open={pinAuthOpen}
        onOpenChange={setPinAuthOpen}
        onComplete={() => {
          setPinAuthOpen(false)
        }}
      />
      <SupportVerificationCodeDialog
        open={supportCodeOpen}
        onOpenChange={setSupportCodeOpen}
      />
    </>
  )

  return { sections, dialogs }
}
